import { CspError } from '@csp/csp-common-model';
import {
  ConsentDocumentContentModel,
  ConsentTextContentModel,
  ConsentDocumentCountryType,
} from '@csp/csp-pod-patient-content-definitions';
import { ConsentType } from '@csp/dmdp-api-user-dto';
import { ArtifactConsentDocumentV1 } from '@csp/dto';
import { CmsConsentDocument } from '../../model/CmsConsentDocument';
import { ScheduledConsent } from '../../model/ScheduledConsent';
import { ScheduledConsentWithDocument } from '../../model/ScheduledConsentWithDocument';
import { ConsentContentService } from '../ConsentContentService';
import { AppWebSystemTextIntersection } from '../../mapper/ConsentResponseMapper';

export const addDocumentsToScheduledConsents = (
  scheduledConsents: ScheduledConsent[],
  consentDocuments: ConsentDocumentContentModel[],
  systemTexts: AppWebSystemTextIntersection,
  consentTexts: Array<ConsentTextContentModel> = [],
): ScheduledConsentWithDocument[] =>
  scheduledConsents.map(scheduledConsent => {
    const document = consentDocuments.find(
      document =>
        document.client_application?.client_value?.includes(
          ConsentContentService.toClientValue(scheduledConsent.documentClientType),
        ) &&
        document.consent_type === scheduledConsent.consentType &&
        document.version === scheduledConsent.version &&
        document.countries?.country?.includes(scheduledConsent.country as ConsentDocumentCountryType),
    );

    if (!document) {
      throw CspError.badState(
        `Unable to identify Consent Document matching configuration: { clientType: ${scheduledConsent.documentClientType}, cmsConsentType: ${scheduledConsent.consentType}, version: ${scheduledConsent.version}, country: ${scheduledConsent.country} }`,
      );
    }

    if (!systemTexts.body?.declineAlert || !systemTexts.button?.readMore) {
      throw CspError.badState(
        `Missing content in generic stack (appPatientConsentScreen.systemTexts): "body.declineAlert" and/or "button.readMore" keys are not set`,
      );
    }

    const cmsConsentDocument = {
      consentType: document.consent_type,
      header: document.header,
      subHeader: document.subheader,
      bodyMd: document.body_text_md,
      declineAlertWarningText: systemTexts.body.declineAlert.text,
      readMoreButtonTextAccessibilityHint: systemTexts.button.readMore.accessibilityHint,
      readMoreButtonTextAccessibilityLabel: systemTexts.button.readMore.accessibilityLabel,
      readMoreButtonText: systemTexts.button.readMore.label,
    };

    return {
      ...scheduledConsent,
      document: toArtifactConsentDocumentV1(cmsConsentDocument, scheduledConsent, systemTexts, consentTexts),
    };
  });

export const toArtifactConsentDocumentV1 = (
  consentDocument: CmsConsentDocument,
  scheduledConsent: ScheduledConsent,
  systemTexts: AppWebSystemTextIntersection,
  consentTexts: ConsentTextContentModel[],
): ArtifactConsentDocumentV1 => {
  const changeOfConsentStatusText = consentTexts
    .flatMap(consentText => consentText.change_of_consent_status)
    .find(
      changeOfConsentStatusBlock =>
        changeOfConsentStatusBlock?.consent_status?.consent_type === scheduledConsent.consentType,
    );

  if (changeOfConsentStatusText?.consent_status) {
    return {
      ...consentDocument,
      acceptDialogHeader: changeOfConsentStatusText.consent_status.accept_header || '',
      declineConfirmationDialogHeader: changeOfConsentStatusText.consent_status.decline_header || '',
      withdrawConfirmationDialogHeader: changeOfConsentStatusText.consent_status.withdrawal_header || '',
      withdrawDataProcessingOptionsDialogBody: changeOfConsentStatusText.consent_status.withdrawal_description || '',
      withdrawDataProcessingOptionsDialogRadioLabel:
        changeOfConsentStatusText.consent_status.withdrawal_options_label || '',
      withdrawDataProcessingOptionsDialogContinueUsingData: {
        accessibilityHint: changeOfConsentStatusText.consent_status.continue_using_data_accessibility_hint || '',
        accessibilityLabel: changeOfConsentStatusText.consent_status.continue_using_data_accessibility_label || '',
        label: changeOfConsentStatusText.consent_status.continue_using_data_label || '',
      },
      withdrawDataProcessingOptionsDialogStopUsingData: {
        accessibilityHint: changeOfConsentStatusText.consent_status.stop_using_data_accessibility_hint || '',
        accessibilityLabel: changeOfConsentStatusText.consent_status.stop_using_data_accessibility_label || '',
        label: changeOfConsentStatusText.consent_status.stop_using_data_label || '',
      },
    };
  }

  // Those should no longer be needed in theory
  // just keeping them for backward compatibility if content is missing from the new "Consent Text" model
  let acceptDialogHeader = systemTexts.header.acceptGeneric.label;
  let declineConfirmationDialogHeader = systemTexts.header.declineGeneric.label;
  let withdrawConfirmationDialogHeader = systemTexts.header.withdrawGeneric.label;
  let withdrawDataProcessingOptionsDialogBody = systemTexts.body.genericWithdrawalModal.markdown;

  switch (scheduledConsent.consentType) {
    case ConsentType.ANALYTICS:
      acceptDialogHeader = systemTexts.header.acceptAnalytics.label;
      declineConfirmationDialogHeader = systemTexts.header.declineAnalytics.label;
      withdrawConfirmationDialogHeader = systemTexts.header.withdrawAnalytics.label;
      withdrawDataProcessingOptionsDialogBody = systemTexts.body.analyticsWithdrawalModal.markdown;
      break;
    case ConsentType.SPFQ_CONSENT:
      acceptDialogHeader = systemTexts.header.acceptSpfq.label;
      declineConfirmationDialogHeader = systemTexts.header.declineSpfq.label;
      withdrawConfirmationDialogHeader = systemTexts.header.withdrawSpfq.label;
      withdrawDataProcessingOptionsDialogBody = systemTexts.body.spfqWithdrawalModal.markdown;
      break;
    case ConsentType.UMUX_CONSENT:
      acceptDialogHeader = systemTexts.header.acceptUmux.text;
      declineConfirmationDialogHeader = systemTexts.header.declineUmux.text;
      withdrawConfirmationDialogHeader = systemTexts.header.withdrawUmux.text;
      withdrawDataProcessingOptionsDialogBody = systemTexts.body.umuxWithdrawalModal.markdown;
      break;
    case ConsentType.CSAT_CONSENT:
      acceptDialogHeader = systemTexts.header.acceptCsat.text;
      declineConfirmationDialogHeader = systemTexts.header.declineCsat.text;
      withdrawConfirmationDialogHeader = systemTexts.header.withdrawCsat.text;
      withdrawDataProcessingOptionsDialogBody = systemTexts.body.csatWithdrawalModal.markdown;
      break;
  }

  return {
    ...consentDocument,
    acceptDialogHeader,
    declineConfirmationDialogHeader,
    withdrawConfirmationDialogHeader,
    withdrawDataProcessingOptionsDialogBody,
    withdrawDataProcessingOptionsDialogRadioLabel: systemTexts.form.radioButton.header.label,
    withdrawDataProcessingOptionsDialogContinueUsingData: {
      accessibilityHint: systemTexts.form.radioButton.continueUsingData.accessibilityHint,
      accessibilityLabel: systemTexts.form.radioButton.continueUsingData.accessibilityLabel,
      label: systemTexts.form.radioButton.continueUsingData.label,
    },
    withdrawDataProcessingOptionsDialogStopUsingData: {
      accessibilityHint: systemTexts.form.radioButton.stopUsingData.accessibilityHint,
      accessibilityLabel: systemTexts.form.radioButton.stopUsingData.accessibilityLabel,
      label: systemTexts.form.radioButton.stopUsingData.label,
    },
  };
};
